import * as React from 'react'
import * as ThemeUI from 'theme-ui'
import * as R from 'ramda'
import * as PropTypes from 'prop-types'
import kebabcase from 'kebab-case'

import * as Forms from '@rushplay/forms'
import * as Herz from '@rushplay/herz'

import * as Utils from './utils'
import { useDebouncedState } from './use-debounced-state'
import { usePrev } from './use-prev'

const validationReasons = [
  'enum',
  'required',
  'pattern',

  'max-properties',
  'min-properties',
  'dependent-required',

  'max-items',
  'min-items',
  'unique-items',
  'max-contains',
  'min-contains',

  'multiple-of',
  'maximum',
  'exclusive-maximum',
  'minimum',
  'exclusive-minimum',

  'max-length',
  'min-length',
  'type',
]

export function FieldResponse(props) {
  const form = Forms.useFormContext()
  const field = Forms.useField(props.scope, { noRegister: true })
  const prevValue = usePrev(field.value)

  const translate = Herz.I18n.useTranslate(
    () =>
      validationReasons.map(
        (reason) => `${form.name}.${kebabcase(field.name)}.${reason}`
      ),
    [field.name, form.name]
  )

  const [showError, setShowError, setShowErrorImmediate] = useDebouncedState(
    800,
    false
  )

  const visited = ![
    Forms.FieldStatus.PRISTINE,
    Forms.FieldStatus.ABSENT,
  ].includes(field.status)

  React.useEffect(() => {
    if (field.errors?.length === 0) {
      setShowErrorImmediate(false)
    } else if (!field.value && visited) {
      setShowErrorImmediate(true)
    } else {
      if (prevValue !== field.value) {
        setShowErrorImmediate(false)
      }
      setShowError(Boolean(field.value))
    }
  }, [field.value, field.errors, visited, prevValue])

  const errors = R.filter((error) => error.translationKey, field.errors)

  const empty = field.value === ''
  const valid = errors?.length === 0
  const visuallyValid = !visited || valid
  const visible = !visuallyValid || (!valid && visited)

  if (!showError || !visible || valid) {
    return null
  }

  const firstError = errors && errors[0]

  return (
    <ThemeUI.Grid
      sx={{
        gridGap: '4px',
        overflow: 'hidden',
        transition: 'opacity 0.5s ease-in-out',
        wordBreak: 'break-word',
        paddingTop: '2px',
        lineHeight: 'xs',
      }}
    >
      <Utils.Components.When condition={firstError && !R.isEmpty(firstError)}>
        <ThemeUI.Text
          sx={{
            fontSize: 'xs',
            color: color({ valid, empty }),
          }}
          key={firstError.translationKey}
        >
          {translate(
            firstError.translationKey,
            firstError.translationVariables
          )}
        </ThemeUI.Text>
      </Utils.Components.When>
    </ThemeUI.Grid>
  )
}

function color(props) {
  if (props.valid && props.empty) {
    return 'gray'
  }

  if (props.valid) {
    return 'success'
  }

  if (!props.valid) {
    return 'danger'
  }

  return 'currentColor'
}

FieldResponse.propTypes = {
  scope: PropTypes.string.isRequired,
}

export default FieldResponse
